import API_BASE_URL from '../../config/api';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Switch,
  Stack,
  Tooltip,
  Divider,
  InputAdornment,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PriceChange as PriceChangeIcon,
  ArrowForward as ArrowForwardIcon,
  Dns as DnsIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';

const BASE_URL = API_BASE_URL;

// The unlock stays valid for the browser tab only, and only for 30 minutes
const UNLOCK_KEY = 'rateAddonUnlockedAt';
const UNLOCK_TTL = 30 * 60 * 1000;

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
  'Content-Type': 'application/json'
});

const decimalsFor = (currency) => (currency === 'USD' ? 2 : 3);

const formatMoney = (value, currency) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(decimalsFor(currency));
};

// What the website will show for a row, using the values currently on screen
const previewRow = (row) => {
  const live = Number(row.livePrice);
  if (row.livePrice === null || row.livePrice === undefined || Number.isNaN(live)) {
    return { addon: null, finalPrice: null };
  }

  const value = parseFloat(row.value);
  if (!row.isActive || !Number.isFinite(value) || value === 0) {
    return { addon: 0, finalPrice: live };
  }

  const addon = row.mode === 'percent' ? (live * value) / 100 : value;
  // A deduction may not take the displayed price below zero
  const finalPrice = Math.max(0, live + addon);
  return { addon: finalPrice - live, finalPrice };
};

const isUnlockValid = () => {
  const stamp = Number(sessionStorage.getItem(UNLOCK_KEY));
  return Boolean(stamp) && (Date.now() - stamp) < UNLOCK_TTL;
};

// ─── Password gate ───────────────────────────────────────────────────────────
const PasswordGate = ({ onUnlock }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUnlock = async () => {
    if (!password) {
      setError('Please enter your admin password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/api/admin/verify-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        sessionStorage.setItem(UNLOCK_KEY, String(Date.now()));
        setPassword('');
        onUnlock();
      } else {
        setError(data.error || 'Incorrect password');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}>
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'white',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <LockIcon fontSize="large" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Protected Screen
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Re-enter your admin password to manage the rate add-on
            </Typography>
          </Box>

          <Collapse in={!!error}>
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          </Collapse>

          <TextField
            fullWidth
            autoFocus
            label="Admin password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleUnlock(); }}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleUnlock}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockOpenIcon />}
            sx={{ mt: 3 }}
          >
            {loading ? 'Verifying...' : 'Unlock'}
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

// ─── Mobile card for a single rate ───────────────────────────────────────────
const RateAddonCard = ({ row, onChange, onReset }) => {
  const { addon, finalPrice } = previewRow(row);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{row.label}</Typography>
            <Typography variant="caption" color="text.secondary">{row.unit}</Typography>
          </Box>
          <Switch
            checked={row.isActive}
            onChange={(e) => onChange('isActive', e.target.checked)}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Live rate</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatMoney(row.livePrice, row.currency)} {row.currency}
            </Typography>
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Add-on type</InputLabel>
            <Select
              value={row.mode}
              label="Add-on type"
              onChange={(e) => onChange('mode', e.target.value)}
            >
              <MenuItem value="fixed">Fixed amount ({row.currency})</MenuItem>
              <MenuItem value="percent">Percentage of live rate (%)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Add-on"
            type="number"
            value={row.value}
            onChange={(e) => onChange('value', e.target.value)}
            inputProps={{ step: row.mode === 'percent' ? 0.01 : 0.001 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {row.mode === 'percent' ? '%' : row.currency}
                </InputAdornment>
              )
            }}
            helperText={row.mode === 'percent'
              ? 'Percentage of the live rate. Use a minus sign to deduct'
              : `Per ${row.unit.split('/')[1] || 'unit'} (0.020 = 20 fils). Use a minus sign to deduct`}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">Website price</Typography>
            <Chip
              color={addon > 0 ? 'success' : addon < 0 ? 'warning' : 'default'}
              label={`${formatMoney(finalPrice, row.currency)} ${row.currency}`}
              sx={{ fontWeight: 700 }}
            />
          </Box>

          <Button size="small" startIcon={<ResetIcon />} onClick={onReset}>
            Clear add-on
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ─── Main screen ─────────────────────────────────────────────────────────────
const RateAddon = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [unlocked, setUnlocked] = useState(isUnlockValid());
  const [rows, setRows] = useState([]);
  const [savedRows, setSavedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ratePriority, setRatePriority] = useState('server1');
  const [savedRatePriority, setSavedRatePriority] = useState('server1');
  const [serverOptions, setServerOptions] = useState([]);
  const [savingPriority, setSavingPriority] = useState(false);

  const toRow = (item) => ({
    metal: item.metal,
    karat: item.karat,
    label: item.label,
    unit: item.unit,
    currency: item.currency,
    livePrice: item.livePrice,
    mode: item.mode || 'fixed',
    value: String(item.value ?? 0),
    isActive: item.isActive !== false,
    updatedBy: item.updatedBy || '',
    updatedAt: item.updatedAt || null
  });

  const applyServerData = (data) => {
    const mapped = (data || []).map(toRow);
    setRows(mapped);
    setSavedRows(mapped);
  };

  const fetchRatePriority = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/rate-priority`, {
        headers: authHeaders()
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setRatePriority(data.data.priority);
        setSavedRatePriority(data.data.priority);
        setServerOptions(data.data.options || []);
      }
    } catch (err) {
      // The add-on table is still usable without this
      setServerOptions([]);
    }
  }, []);

  const handleSaveRatePriority = async (nextPriority) => {
    const previous = savedRatePriority;

    setRatePriority(nextPriority);
    setSavingPriority(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${BASE_URL}/api/admin/rate-priority`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ priority: nextPriority })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setSavedRatePriority(nextPriority);
        setSuccess('Priority updated - reloading the live rates');
        await fetchMarkups();
      } else {
        throw new Error(data.error || 'Failed to update the priority');
      }
    } catch (err) {
      setRatePriority(previous);
      setError(err.message || 'Network error occurred');
    } finally {
      setSavingPriority(false);
    }
  };

  const fetchMarkups = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/api/admin/rate-markups`, {
        headers: authHeaders()
      });
      const data = await response.json();

      if (response.ok && data.success) {
        applyServerData(data.data);
      } else {
        throw new Error(data.error || 'Failed to load rate add-ons');
      }
    } catch (err) {
      setError(err.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    fetchRatePriority();
    fetchMarkups();
  }, [unlocked, fetchRatePriority, fetchMarkups]);

  const handleChange = (index, field, value) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSave = async () => {
    const invalid = rows.find(row => {
      const value = parseFloat(row.value);
      if (!Number.isFinite(value)) return true;
      if (row.mode === 'percent' && value <= -100) return true;
      return Math.abs(value) > (row.mode === 'percent' ? 100 : 1000);
    });

    if (invalid) {
      setError(`${invalid.label}: enter an amount between ${invalid.mode === 'percent' ? '-99.99% and 100%' : '-1000 and 1000'}`);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${BASE_URL}/api/admin/rate-markups`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          markups: rows.map(row => ({
            metal: row.metal,
            karat: row.karat,
            mode: row.mode,
            value: parseFloat(row.value) || 0,
            isActive: row.isActive
          }))
        })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        applyServerData(data.data);
        setSuccess('Rate add-ons saved — the website is now using the new prices');
      } else {
        throw new Error((data.details && data.details.join(', ')) || data.error || 'Failed to save rate add-ons');
      }
    } catch (err) {
      setError(err.message || 'Network error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleResetRow = (index) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, value: '0', mode: 'fixed' } : row)));
  };

  const handleLock = () => {
    sessionStorage.removeItem(UNLOCK_KEY);
    setUnlocked(false);
  };

  const isDirty = JSON.stringify(rows) !== JSON.stringify(savedRows);

  const lastUpdated = savedRows
    .filter(row => row.updatedAt)
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Rate Add-on
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The amount added on top of the live market rate before it is shown on the website
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Reload live rates">
            <span>
              <IconButton onClick={fetchMarkups} disabled={loading || saving}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="outlined" startIcon={<LockIcon />} onClick={handleLock}>
            Lock
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading || !isDirty}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </Stack>
      </Box>

      {/* How it works */}
      <Alert severity="info" icon={<PriceChangeIcon />} sx={{ mb: 2 }}>
        <AlertTitle>How the add-on works</AlertTitle>
        Live rate <strong>40.000 KD</strong> + fixed add-on <strong>0.020 KD</strong> (20 fils) shows as{' '}
        <strong>40.020 KD</strong> on the website. A percentage add-on of <strong>0.5%</strong> on the same
        rate shows as <strong>40.200 KD</strong>. Enter a <strong>negative</strong> amount to go below the
        live rate: <strong>-0.020</strong> shows as <strong>39.980 KD</strong>. The add-on applies to the
        prices on the home page and to every product priced from that rate.
      </Alert>

      {/* Which server takes priority for the live rates */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DnsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Rate priority
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Both servers are used automatically. The one you choose here wins wherever
            both publish a price; the other fills the gaps and takes over on its own if
            the preferred server stops responding.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <FormControl sx={{ minWidth: 280 }} size="small" disabled={savingPriority}>
              <InputLabel>Preferred server</InputLabel>
              <Select
                value={ratePriority}
                label="Preferred server"
                onChange={(e) => handleSaveRatePriority(e.target.value)}
              >
                {serverOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {savingPriority && <CircularProgress size={20} />}

            {serverOptions
              .filter((option) => option.value === ratePriority)
              .map((option) => (
                <Typography key={option.value} variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {option.description}
                </Typography>
              ))}
          </Stack>

          {serverOptions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              {serverOptions.map((option) => (
                <Chip
                  key={option.value}
                  size="small"
                  variant="outlined"
                  color={option.reachable ? 'success' : 'warning'}
                  icon={option.reachable ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                  label={`${option.label}: ${option.reachable ? 'reachable' : 'not responding'}`}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Collapse in={!!error}>
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Collapse>

      <Collapse in={!!success}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      </Collapse>

      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box>
          {rows.map((row, index) => (
            <RateAddonCard
              key={`${row.metal}-${row.karat}`}
              row={row}
              onChange={(field, value) => handleChange(index, field, value)}
              onReset={() => handleResetRow(index)}
            />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>Rate</TableCell>
                <TableCell align="right">Live rate</TableCell>
                <TableCell>Add-on type</TableCell>
                <TableCell align="right">Add-on (+/-)</TableCell>
                <TableCell align="right">Website price</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="center">Clear</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const { addon, finalPrice } = previewRow(row);

                return (
                  <TableRow key={`${row.metal}-${row.karat}`} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.unit}</Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatMoney(row.livePrice, row.currency)}
                      </Typography>
                      {row.livePrice === null && (
                        <Typography variant="caption" color="error">unavailable</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ minWidth: 190 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={row.mode}
                          onChange={(e) => handleChange(index, 'mode', e.target.value)}
                        >
                          <MenuItem value="fixed">Fixed ({row.currency})</MenuItem>
                          <MenuItem value="percent">Percent (%)</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>

                    <TableCell align="right" sx={{ minWidth: 160 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={row.value}
                        onChange={(e) => handleChange(index, 'value', e.target.value)}
                        inputProps={{
                          step: row.mode === 'percent' ? 0.01 : 0.001,
                          style: { textAlign: 'right' }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {row.mode === 'percent' ? '%' : row.currency}
                            </InputAdornment>
                          )
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <Typography variant="caption" color="text.secondary">
                          {addon ? `${addon > 0 ? '+' : '-'}${formatMoney(Math.abs(addon), row.currency)}` : ''}
                        </Typography>
                        {!!addon && <ArrowForwardIcon fontSize="inherit" color="disabled" />}
                        <Chip
                          size="small"
                          color={addon > 0 ? 'success' : addon < 0 ? 'warning' : 'default'}
                          label={`${formatMoney(finalPrice, row.currency)} ${row.currency}`}
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>
                    </TableCell>

                    <TableCell align="center">
                      <Switch
                        checked={row.isActive}
                        onChange={(e) => handleChange(index, 'isActive', e.target.checked)}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Clear add-on">
                        <IconButton size="small" onClick={() => handleResetRow(index)}>
                          <ResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No rates available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {lastUpdated && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Last updated by {lastUpdated.updatedBy || 'admin'} on{' '}
          {new Date(lastUpdated.updatedAt).toLocaleString()}
        </Typography>
      )}
    </Container>
  );
};

export default RateAddon;
